import { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, FileText, Plus, Trash2 } from 'lucide-react';
import {
  CLIENT_ADDRESS_TOKEN,
  QUOTE_UNIT_SUGGESTIONS,
  documentUsesToken,
  blocksToText,
  lineItemAmount,
  quoteTotals,
  splitDeposit,
  textToBlocks,
  isHoursUnit,
  type DeliveryMilestone,
  type QuoteDocument,
  type QuotePlanOption,
  type QuoteUnit,
} from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';
import { QuoteSendPanel } from './QuoteSendPanel.tsx';
import { QuoteBlocksView } from './QuoteBlockEditor.tsx';
import { DeliveryPlanEditor } from './DeliveryPlanEditor.tsx';
import type { SowCurrent, SowDraft } from './QuoteSowChat.tsx';

// The quote, as the client will see it, with the parts you can change
// clickable in place.
//
// IT LOOKS LIKE THE DOCUMENT, NOT LIKE A FORM. The earlier version broke
// every section into a stack of little boxes with type dropdowns — one
// per paragraph, one per list — which is not how anyone writes a
// paragraph of scope and made the whole thing unreadable. A section is
// now one piece of text: click it, edit it, click away.
//
// The markers are the ones people already type. "- " is a bullet, "1. "
// a numbered step, "## " a subheading. `blocksToText` and `textToBlocks`
// are exact inverses, so opening a section and closing it untouched
// changes nothing.
//
// The pricing table stays a table, because it is one.

interface Props {
  ticketId: string;
  ticketSubject: string;
  /** Refresh the ticket — its quote custom fields change on every save. */
  onSaved: () => void | Promise<void>;
  /** Lets the page mark the ticket's quote fields as document-owned. */
  onDocumentPresenceChange?: (present: boolean) => void;
  /**
   * A draft handed in from the scoping chat. Loaded into the editor (not
   * saved) whenever `incomingSeq` changes — a counter rather than the
   * object itself, so applying the SAME draft twice still works.
   */
  incoming?: IncomingDraft | null;
  incomingSeq?: number;
  /** Reports the scope, hours and plan as they stand, so the chat revises rather than redrafts. */
  onSectionsChange?: (sow: SowCurrent) => void;
}

export type IncomingDraft = SowDraft;

/** A section while it is being edited: heading plus one block of text. */
interface DraftSection {
  heading: string;
  text: string;
  /** Renders closed on the client page. The agreements use it. */
  collapsible: boolean;
  /** The plans or the priced table render inside this section. */
  showsPricing: boolean;
}

interface DraftLineItem {
  description: string;
  quantity: string;
  unit: QuoteUnit;
  unitPriceUsd: string;
}

interface Draft {
  title: string;
  intro: string;
  closing: string;
  sections: DraftSection[];
  lineItems: DraftLineItem[];
  /** Internal project budget when no line is priced in hours. */
  estimatedHours: string;
  /** Percent taken at signature. The house default is 50. */
  depositPercent: string;
  /**
   * Plans the client chooses between. Carried through the editor
   * whether or not it can change them: a block-hours quote IS its
   * ladder, and a save that dropped it would leave a document with
   * no pricing at all.
   */
  planOptions: QuotePlanOption[];
  /** Milestones and tasks the project gets on signature. Round-tripped always. */
  deliveryPlan: DeliveryMilestone[];
}

function emptyDraft(ticketSubject: string): Draft {
  return {
    title: ticketSubject,
    intro: '',
    closing: '',
    sections: [{ heading: 'Scope of work', text: '', collapsible: false, showsPricing: false }],
    lineItems: [{ description: '', quantity: '', unit: 'hours', unitPriceUsd: '' }],
    estimatedHours: '',
    depositPercent: '50',
    planOptions: [],
    deliveryPlan: [],
  };
}

function draftFrom(doc: QuoteDocument): Draft {
  return {
    title: doc.title,
    intro: doc.intro ?? '',
    closing: doc.closing ?? '',
    sections: doc.sections.map((s) => ({
      heading: s.heading,
      text: blocksToText(s.blocks),
      collapsible: s.collapsible,
      // Optional on the wire: documents written before it existed.
      showsPricing: s.showsPricing ?? false,
    })),
    lineItems: doc.lineItems.map((i) => ({
      description: i.description,
      quantity: String(i.quantity),
      unit: i.unit,
      unitPriceUsd: i.unitPriceUsd === null ? '' : String(i.unitPriceUsd),
    })),
    estimatedHours: doc.estimatedHours === null ? '' : String(doc.estimatedHours),
    depositPercent: String(doc.depositPercent),
    planOptions: doc.planOptions ?? [],
    deliveryPlan: doc.deliveryPlan ?? [],
  };
}

const money = (value: number): string =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** A row with nothing at all in it is ignored; anything else is the agent's. */
const rowIsBlank = (r: DraftLineItem): boolean =>
  !r.description.trim() && !r.quantity.trim() && !r.unitPriceUsd.trim();

/**
 * Read the pricing table.
 *
 * NOTHING THE AGENT TYPED IS SILENTLY DROPPED. This used to skip any row
 * whose quantity or price did not parse, which meant a flat-rate line
 * entered as a description and a price — with the quantity left blank,
 * the natural way to write one — vanished on save with no message. A
 * quote that comes back missing a line is far worse than a save that
 * says what is wrong.
 *
 * A blank quantity means one of the thing, and a blank price means it is
 * included at no charge. Both are real answers. What is NOT guessed at is
 * a row with content but no description, or one with text where a number
 * should be: those are reported and the save is refused.
 */
function readRows(rows: DraftLineItem[]): {
  items: Array<{ description: string; quantity: number; unit: QuoteUnit; unitPriceUsd: number }>;
  problem: string | null;
} {
  const items: Array<{
    description: string;
    quantity: number;
    unit: QuoteUnit;
    unitPriceUsd: number;
  }> = [];
  let problem: string | null = null;

  rows.forEach((row, index) => {
    if (rowIsBlank(row)) return;
    const where = `Line ${index + 1}`;
    const quantity = row.quantity.trim() === '' ? 1 : Number(row.quantity);
    const unitPriceUsd = row.unitPriceUsd.trim() === '' ? 0 : Number(row.unitPriceUsd);

    if (!row.description.trim()) {
      problem ??= `${where} has a price but no description. Describe it or clear the row.`;
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      problem ??= `${where} ("${row.description.trim()}") needs a quantity above zero.`;
      return;
    }
    if (!Number.isFinite(unitPriceUsd) || unitPriceUsd < 0) {
      problem ??= `${where} ("${row.description.trim()}") needs a price of zero or more.`;
      return;
    }
    items.push({
      description: row.description.trim(),
      quantity,
      unit: row.unit.trim() || 'each',
      unitPriceUsd,
    });
  });

  return { items, problem };
}

/** The rows that currently price, for the running totals on screen. */
const pricedRows = (rows: DraftLineItem[]): ReturnType<typeof readRows>['items'] =>
  readRows(rows).items;

// THE AGREEMENTS ARE NOT SHOWN HERE. The MNDA, the MSA and its summary
// are the rolled-up sections a template brings; they are the same
// contract for every client, they are fixed (the server keeps the stored
// copies whatever a save sends), and they are edited on the template.
// They stay in the draft untouched so a save carries them through, and
// the client sees them - filled in with their company - on the quote.
// Numbering and moving below count only the sections on screen.

/** The 1-based position of a section among the ones on screen. */
function visibleNumber(sections: ReadonlyArray<{ collapsible: boolean }>, index: number): number {
  return sections.slice(0, index + 1).filter((s) => !s.collapsible).length;
}

/** Swap a section with its nearest on-screen neighbour, past any hidden ones. */
function moveVisible<T extends { collapsible: boolean }>(list: T[], index: number, direction: -1 | 1): T[] {
  let target = index + direction;
  while (target >= 0 && target < list.length && list[target]!.collapsible) target += direction;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}


const inputClass =
  'block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30';

export function QuoteDocumentPanel({
  ticketId,
  ticketSubject,
  onSaved,
  onDocumentPresenceChange,
  incoming,
  incomingSeq = 0,
  onSectionsChange,
}: Props): React.ReactElement {
  const [doc, setDoc] = useState<QuoteDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  /** Which section is open for editing, or null. Only ever one. */
  const [editing, setEditing] = useState<number | null>(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [sendReloadKey, setSendReloadKey] = useState(0);

  const update = (next: Draft): void => {
    setDraft(next);
    setDirty(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await apiFetch<{ document: QuoteDocument | null }>(
        `/api/tickets/${ticketId}/quote-document`,
      );
      setDoc(data.document);
      onDocumentPresenceChange?.(data.document !== null);
      if (data.document) setDraft((current) => current ?? draftFrom(data.document!));
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Could not load the quote document.');
    } finally {
      setLoading(false);
    }
  }, [ticketId, onDocumentPresenceChange]);

  useEffect(() => {
    void load();
  }, [load]);

  // The chat needs the quote as it stands on screen: the scope as
  // blocks, the hours lines and the plan - so an hour the agent changed
  // by hand is revised from, not re-estimated over.
  useEffect(() => {
    if (!draft) return;
    onSectionsChange?.({
      // Scope only: the agreements are not the chat's to read or revise.
      sections: draft.sections.filter((s) => !s.collapsible).map((s) => ({
        heading: s.heading,
        blocks: textToBlocks(s.text),
      })),
      effort: draft.lineItems
        .filter((r) => isHoursUnit(r.unit) && r.description.trim() && Number(r.quantity) > 0)
        .map((r) => ({ area: r.description.trim(), hours: Number(r.quantity) })),
      deliveryPlan: draft.deliveryPlan.map((m) => ({
        title: m.title,
        dueAfterDays: m.dueAfterDays,
        tasks: m.tasks.map((t) => t.title),
      })),
      pricedByPlans: draft.planOptions.length > 0,
    });
  }, [draft, onSectionsChange]);

  // A draft from the chat replaces the scope, the hours lines and the
  // plan. Everything else - the rate, lines priced in other units, the
  // title, the deposit and the terms - is the agent's and carried across.
  useEffect(() => {
    if (!incoming || incomingSeq === 0) return;
    setDraft((current) => {
      const base = current ?? (doc ? draftFrom(doc) : emptyDraft(ticketSubject));
      const pricedByPlans = base.planOptions.length > 0;
      const effort = pricedByPlans ? [] : (incoming.effort ?? []);
      const plan = pricedByPlans ? [] : (incoming.deliveryPlan ?? []);
      // The rate already on the quote's hours line - the template's, or
      // the agent's - prices the new lines. With none it stays blank and
      // the table says so: Claude estimates work, it never sets a rate.
      const rate =
        base.lineItems.find((r) => isHoursUnit(r.unit) && r.unitPriceUsd.trim() !== '')?.unitPriceUsd ?? '';
      return {
        ...base,
        // An empty estimate leaves the table alone rather than wiping it.
        lineItems:
          effort.length === 0
            ? base.lineItems
            : [
                ...effort.map((e) => ({
                  description: e.area,
                  quantity: String(e.hours),
                  unit: 'hours',
                  unitPriceUsd: rate,
                })),
                ...base.lineItems.filter((r) => !isHoursUnit(r.unit) && !rowIsBlank(r)),
              ],
        deliveryPlan: plan.length === 0 ? base.deliveryPlan : plan,
        sections: [
          ...incoming.sections.map((s) => ({
            heading: s.heading,
            text: blocksToText(s.blocks),
            collapsible: false,
            showsPricing: false,
          })),
          // The agreements are not scope, and not the chat's to replace.
          ...base.sections.filter((s) => s.collapsible),
        ],
      };
    });
    setDirty(true);
    setEditing(null);
    setSaveError(null);
    // Keyed on the counter alone: re-running when `incoming` or `doc`
    // merely changed identity would throw away edits already made.
  }, [incomingSeq]);

  async function save(): Promise<void> {
    if (!draft) return;

    // REFUSED, NOT DISCARDED. Both of these used to be silent filters in
    // the request body: a section with no heading was dropped with its
    // whole body, and a line item that did not parse vanished. The agent
    // saw a save succeed and the text they had just written gone. A quote
    // is the document the work is sold on; losing a paragraph of scope
    // quietly is the worst thing this editor can do.
    const emptyHeading = draft.sections.findIndex(
      (sec) => !sec.heading.trim() && sec.text.trim() !== '',
    );
    if (emptyHeading !== -1) {
      setSaveError(
        `Section ${visibleNumber(draft.sections, emptyHeading)} has text but no heading. Give it a heading and save again — nothing has been lost.`,
      );
      setEditing(emptyHeading);
      return;
    }
    const { items: lineItems, problem } = readRows(draft.lineItems);
    if (problem) {
      setSaveError(problem);
      return;
    }

    setBusy(true);
    setSaveError(null);
    try {
      const data = await apiFetch<{ document: QuoteDocument }>(
        `/api/tickets/${ticketId}/quote-document`,
        {
          method: 'PUT',
          json: {
            title: draft.title.trim(),
            intro: draft.intro.trim() || null,
            closing: draft.closing.trim() || null,
            // Only genuinely empty sections are dropped — one with a
            // heading and no body is legitimate (a heading over line
            // items), and one with a body and no heading was refused
            // above rather than thrown away.
            sections: draft.sections
              .filter((s) => s.heading.trim() || s.text.trim())
              .map((s) => ({
                heading: s.heading.trim(),
                blocks: textToBlocks(s.text),
                collapsible: s.collapsible,
                showsPricing: s.showsPricing,
              })),
            lineItems,
            planOptions: draft.planOptions,
            deliveryPlan: draft.deliveryPlan,
            estimatedHours: Number(draft.estimatedHours) > 0 ? Number(draft.estimatedHours) : null,
            depositPercent: Number(draft.depositPercent) || 0,
          },
        },
      );
      setDoc(data.document);
      setDraft(draftFrom(data.document));
      setDirty(false);
      setEditing(null);
      setSendReloadKey((k) => k + 1);
      onDocumentPresenceChange?.(true);
      await onSaved();
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : 'Could not save the quote.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!window.confirm('Delete this quote document? The hours and SOW already on the ticket stay.')) {
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      await apiFetch(`/api/tickets/${ticketId}/quote-document`, { method: 'DELETE' });
      setDoc(null);
      setDraft(null);
      onDocumentPresenceChange?.(false);
      await onSaved();
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : 'Could not delete the quote document.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-[var(--color-muted)]">Loading the quote…</p>;
  }

  if (loadError) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-[var(--color-danger)]">{loadError}</p>
        <Button variant="outline" onClick={() => void load()}>
          Try again
        </Button>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="rounded-[var(--radius-button)] border border-dashed border-[var(--color-border)] bg-white p-6 text-center">
        <FileText className="mx-auto h-6 w-6 text-[var(--color-muted)]" aria-hidden="true" />
        <p className="mt-2 text-sm font-medium text-[var(--color-header)]">Nothing scoped yet</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-[var(--color-muted)]">
          Talk it through above and the scope of work appears here, or start it by hand.
        </p>
        <div className="mt-4">
          <Button onClick={() => update(emptyDraft(ticketSubject))}>Start by hand</Button>
        </div>
      </div>
    );
  }

  const priced = pricedRows(draft.lineItems);
  const totals = quoteTotals(priced);
  // Shown while editing so the deposit is never a surprise at signature.
  // Same function the server charges with, on the same cents.
  const depositSplit =
    totals.amountUsd > 0
      ? splitDeposit(Math.round(totals.amountUsd * 100), Number(draft.depositPercent) || 0)
      : null;

  return (
    <div className="space-y-6">
      {doc &&
        !accepted &&
        !doc.organizationAddress &&
        documentUsesToken(doc, CLIENT_ADDRESS_TOKEN) && (
          <ClientAddressPrompt
            organizationId={doc.organizationId}
            organizationName={doc.organizationName}
            // A quiet refetch, not `load`: that one shows the full-page
            // loading state, which would blank the quote being written.
            onSaved={async () => {
              const data = await apiFetch<{ document: QuoteDocument | null }>(
                `/api/tickets/${ticketId}/quote-document`,
              );
              setDoc(data.document);
            }}
          />
        )}

      {/* The document, as the client reads it. --------------------------- */}
      <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white p-6">
        {editingHeader ? (
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Quote title
              </span>
              <input
                value={draft.title}
                onChange={(e) => update({ ...draft, title: e.target.value })}
                className={inputClass}
                autoFocus
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Introduction <span className="font-normal text-[var(--color-muted)]">(optional)</span>
              </span>
              <textarea
                rows={3}
                value={draft.intro}
                onChange={(e) => update({ ...draft, intro: e.target.value })}
                className={inputClass}
              />
            </label>
            <Button variant="outline" onClick={() => setEditingHeader(false)}>
              Done
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => !accepted && setEditingHeader(true)}
            className={`block w-full border-b border-[var(--color-border)] pb-4 text-left ${
              accepted ? '' : 'rounded hover:bg-[var(--color-surface)]'
            }`}
          >
            <h1 className="text-2xl font-bold text-[var(--color-header)]">{draft.title}</h1>
            {draft.intro && (
              <p className="mt-2 whitespace-pre-wrap text-[15px] leading-relaxed text-[var(--color-body)]">
                {draft.intro}
              </p>
            )}
          </button>
        )}

        <div className="mt-6 space-y-5">
          {draft.sections.map((section, index) => {
            if (section.collapsible) return null;
            const n = visibleNumber(draft.sections, index);
            const visibleCount = draft.sections.filter((s) => !s.collapsible).length;
            return editing === index ? (
              <div
                key={index}
                className="rounded-[var(--radius-button)] border border-[var(--color-t3-blue)] bg-[var(--color-surface)] p-4"
              >
                <div className="flex items-center gap-2">
                  <input
                    value={section.heading}
                    onChange={(e) => {
                      const sections = [...draft.sections];
                      sections[index] = { ...section, heading: e.target.value };
                      update({ ...draft, sections });
                    }}
                    className={`${inputClass} font-semibold`}
                    placeholder="Section heading"
                    aria-label={`Section ${n} heading`}
                  />
                  <button
                    type="button"
                    onClick={() => update({ ...draft, sections: moveVisible(draft.sections, index, -1) })}
                    disabled={n === 1}
                    className="rounded p-1.5 text-[var(--color-muted)] hover:bg-white disabled:opacity-30"
                    aria-label={`Move section ${n} up`}
                  >
                    <ArrowUp className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => update({ ...draft, sections: moveVisible(draft.sections, index, 1) })}
                    disabled={n === visibleCount}
                    className="rounded p-1.5 text-[var(--color-muted)] hover:bg-white disabled:opacity-30"
                    aria-label={`Move section ${n} down`}
                  >
                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                  {/* Where the plans or the priced table sit on the client
                      page. One section at most, so ticking it here moves
                      it rather than adding a second. (No "Rolled up" here:
                      rolled-up sections are the template's agreements.) */}
                  <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-[var(--color-muted)]">
                    <input
                      type="checkbox"
                      checked={section.showsPricing}
                      onChange={(e) => {
                        const sections = draft.sections.map((s, i) => ({
                          ...s,
                          showsPricing: i === index ? e.target.checked : false,
                        }));
                        update({ ...draft, sections });
                      }}
                      className="h-4 w-4 rounded border-[var(--color-border)]"
                    />
                    Pricing here
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      update({ ...draft, sections: draft.sections.filter((_, i) => i !== index) });
                      setEditing(null);
                    }}
                    className="rounded p-1.5 text-[var(--color-muted)] hover:bg-rose-50 hover:text-[var(--color-danger)]"
                    aria-label={`Remove section ${n}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                <textarea
                  rows={Math.min(24, Math.max(6, section.text.split('\n').length + 2))}
                  value={section.text}
                  onChange={(e) => {
                    const sections = [...draft.sections];
                    sections[index] = { ...section, text: e.target.value };
                    update({ ...draft, sections });
                  }}
                  className={`${inputClass} mt-2 font-[inherit]`}
                  placeholder={'What we will do, in the client’s language.\n\n- A bullet\n- Another bullet\n\n1. A numbered step\n\n## A subheading'}
                  aria-label={`Section ${n} text`}
                  autoFocus
                />
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Start a line with <code>-</code> for a bullet, <code>1.</code> for a numbered
                  step, <code>##</code> for a subheading. Indent three spaces to nest a list item.
                  Wrap words in <code>**</code> for bold. A line starting with <code>\</code> stays
                  plain text. <code>[CLIENT NAME]</code> and <code>[CLIENT ADDRESS]</code> are filled
                  from the client record when the quote is sent.
                </p>
                <div className="mt-2">
                  <Button variant="outline" onClick={() => setEditing(null)}>
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <button
                key={index}
                type="button"
                onClick={() => !accepted && setEditing(index)}
                className={`block w-full text-left ${
                  accepted ? '' : 'rounded p-2 -m-2 hover:bg-[var(--color-surface)]'
                }`}
              >
                <h2 className="flex flex-wrap items-center gap-2 text-base font-semibold text-[var(--color-header)]">
                  {section.heading || 'Untitled section'}
                  {section.showsPricing && (
                    <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-muted)]">
                      Pricing here
                    </span>
                  )}
                </h2>
                <div className="mt-1">
                  <QuoteBlocksView blocks={textToBlocks(section.text)} />
                </div>
              </button>
            );
          })}
        </div>

        {!accepted && (
          <button
            type="button"
            onClick={() => {
              update({ ...draft, sections: [...draft.sections, { heading: '', text: '', collapsible: false, showsPricing: false }] });
              setEditing(draft.sections.length);
            }}
            className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
          >
            <Plus className="h-4 w-4" aria-hidden="true" /> Add a section
          </button>
        )}

        {/* Pricing. Either a ladder the client picks from, or a table
            this quote prices itself. Never both - the server refuses a
            document carrying two answers to what it costs. */}
        {draft.planOptions.length > 0 ? (
          <PlanEditor
            plans={draft.planOptions}
            readOnly={accepted}
            onChange={(planOptions) => update({ ...draft, planOptions })}
          />
        ) : (
        <div className="mt-8">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-base font-semibold text-[var(--color-header)]">Pricing</h2>
            {!accepted && (
              <button
                type="button"
                onClick={() =>
                  update({
                    ...draft,
                    lineItems: [
                      ...draft.lineItems,
                      { description: '', quantity: '', unit: 'hours', unitPriceUsd: '' },
                    ],
                  })
                }
                className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-t3-blue)] hover:underline"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add line
              </button>
            )}
          </div>

          <datalist id="quote-units">
            {QUOTE_UNIT_SUGGESTIONS.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>

          <div className="overflow-x-auto rounded-[var(--radius-button)] border border-[var(--color-border)]">
            <table className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th className="px-3 py-2 font-semibold">Description</th>
                  <th className="w-24 px-3 py-2 font-semibold">Qty</th>
                  <th className="w-32 px-3 py-2 font-semibold">Unit</th>
                  <th className="w-32 px-3 py-2 font-semibold">Unit price</th>
                  <th className="w-28 px-3 py-2 text-right font-semibold">Amount</th>
                  <th className="w-10 px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {draft.lineItems.map((item, index) => {
                  const quantity = Number(item.quantity);
                  const price = Number(item.unitPriceUsd);
                  const complete =
                    Number.isFinite(quantity) && quantity > 0 && Number.isFinite(price) && price >= 0;
                  return (
                    <tr key={index} className="border-b border-[var(--color-border)] last:border-0">
                      <td className="px-3 py-2">
                        <input
                          value={item.description}
                          onChange={(e) => {
                            const lineItems = [...draft.lineItems];
                            lineItems[index] = { ...item, description: e.target.value };
                            update({ ...draft, lineItems });
                          }}
                          className={inputClass}
                          placeholder="Mailbox migration"
                          aria-label={`Line ${index + 1} description`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          step="0.25"
                          value={item.quantity}
                          onChange={(e) => {
                            const lineItems = [...draft.lineItems];
                            lineItems[index] = { ...item, quantity: e.target.value };
                            update({ ...draft, lineItems });
                          }}
                          className={inputClass}
                          aria-label={`Line ${index + 1} quantity`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          list="quote-units"
                          value={item.unit}
                          onChange={(e) => {
                            const lineItems = [...draft.lineItems];
                            lineItems[index] = { ...item, unit: e.target.value };
                            update({ ...draft, lineItems });
                          }}
                          className={inputClass}
                          placeholder="hours"
                          aria-label={`Line ${index + 1} unit`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPriceUsd}
                          onChange={(e) => {
                            const lineItems = [...draft.lineItems];
                            lineItems[index] = { ...item, unitPriceUsd: e.target.value };
                            update({ ...draft, lineItems });
                          }}
                          className={inputClass}
                          aria-label={`Line ${index + 1} unit price`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-[var(--color-body)]">
                        {complete ? money(lineItemAmount({ quantity, unitPriceUsd: price })) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() =>
                            update({
                              ...draft,
                              lineItems: draft.lineItems.filter((_, i) => i !== index),
                            })
                          }
                          className="rounded p-1.5 text-[var(--color-muted)] hover:bg-rose-50 hover:text-[var(--color-danger)]"
                          aria-label={`Remove line ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[var(--color-surface)] text-base font-bold text-[var(--color-header)]">
                  <td className="px-3 py-3" colSpan={4}>
                    Total{totals.hours > 0 ? ` · ${totals.hours} h` : ''}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">{money(totals.amountUsd)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* The chat writes hours, never a rate. A blank price reads as
              "included at no charge", which is a real answer on a line
              the agent priced on purpose and a $0 quote on one nobody did. */}
          {(() => {
            const unrated = draft.lineItems.filter(
              (r) => isHoursUnit(r.unit) && r.description.trim() && r.quantity.trim() && !r.unitPriceUsd.trim(),
            ).length;
            return unrated > 0 && !accepted ? (
              <div className="mt-3 rounded-[var(--radius-button)] border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {unrated === 1 ? '1 hours line has' : `${unrated} hours lines have`} no unit price. Set the
                hourly rate, or the line is quoted as included at no charge.
              </div>
            ) : null;
          })()}

          <p className="mt-2 text-xs text-[var(--color-muted)]">
            Only <em>hours</em> counts toward the project budget. Any other unit is just the word
            the client reads — <em>mailboxes</em>, <em>devices</em>, <em>sites</em>.
          </p>

          {/* ALWAYS SHOWN. This used to appear only when nothing was
              priced by the hour, so the moment a single hours line was
              added the field vanished — taking the number already typed
              out of sight, which reads exactly like losing it. An
              internal estimate is also a legitimate thing to record
              against an hours-priced quote. */}
          <label className="mt-3 block max-w-xs">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Estimated hours{' '}
                <span className="font-normal text-[var(--color-muted)]">(internal)</span>
              </span>
              <input
                type="number"
                min="0"
                step="0.25"
                value={draft.estimatedHours}
                onChange={(e) => update({ ...draft, estimatedHours: e.target.value })}
                className={inputClass}
                placeholder="e.g. 12"
              />
            <span className="mt-1 block text-xs text-[var(--color-muted)]">
              {totals.hours > 0
                ? `The ${totals.hours} priced hours are the project budget; this is your own estimate alongside it. The client never sees either.`
                : 'Nothing is priced by the hour, so this becomes the project budget. The client never sees it.'}
            </span>
          </label>

          {/* What the client pays to sign. 50/50 is the house term; block
              hours are invoiced rather than taken up front, so 0 has to
              be reachable without it looking like a mistake. */}
          <div className="mt-4 border-t border-[var(--color-border)] pt-4">
            <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
              Deposit at signature
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {['50', '100', '0'].map((pct) => (
                <button
                  key={pct}
                  type="button"
                  onClick={() => update({ ...draft, depositPercent: pct })}
                  className={
                    draft.depositPercent === pct
                      ? 'rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-3 py-1.5 text-sm font-semibold text-white'
                      : 'rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-body)] hover:border-[var(--color-t3-blue)]'
                  }
                >
                  {pct === '0' ? 'None' : `${pct}%`}
                </button>
              ))}
              <label className="flex items-center gap-1.5">
                <span className="sr-only">Deposit percentage</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={draft.depositPercent}
                  onChange={(e) => update({ ...draft, depositPercent: e.target.value })}
                  className="w-20 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-1.5 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
                />
                <span className="text-sm text-[var(--color-muted)]">%</span>
              </label>
            </div>
            <p className="mt-2 text-xs text-[var(--color-muted)]">
              {depositSplit
                ? depositSplit.depositCents === 0
                  ? 'No card is taken at signature; the work is invoiced.'
                  : `${money(depositSplit.depositCents / 100)} due to accept, ${money(
                      depositSplit.balanceCents / 100,
                    )} on completion. The card is kept on file for the balance.`
                : 'Taken by card on the quote page before the signature is recorded.'}
            </p>
          </div>
        </div>
        )}

        {/* What the signed SOW becomes. Not on a plan quote: block hours
            are a subscription and never become a project. */}
        {draft.planOptions.length === 0 && (
          <DeliveryPlanEditor
            plan={draft.deliveryPlan}
            readOnly={accepted}
            onChange={(deliveryPlan) => update({ ...draft, deliveryPlan })}
          />
        )}

        {/* Terms. --------------------------------------------------------- */}
        <div className="mt-6 border-t border-[var(--color-border)] pt-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
              Terms / next steps{' '}
              <span className="font-normal text-[var(--color-muted)]">(optional)</span>
            </span>
            <textarea
              rows={3}
              value={draft.closing}
              onChange={(e) => update({ ...draft, closing: e.target.value })}
              className={inputClass}
            />
          </label>
        </div>
      </div>

      {saveError && <p className="text-sm text-[var(--color-danger)]">{saveError}</p>}

      <div className="flex flex-wrap items-center gap-3">
        {accepted ? (
          <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
            Signed — locked
          </span>
        ) : (
          <>
            <Button onClick={() => void save()} disabled={busy || !dirty}>
              {busy ? 'Saving…' : dirty ? 'Save quote' : 'Saved'}
            </Button>
            {doc && (
              <Button variant="danger" onClick={() => void remove()} disabled={busy}>
                Delete
              </Button>
            )}
          </>
        )}
        {dirty && !accepted && (
          <span className="text-xs text-[var(--color-muted)]">Unsaved changes</span>
        )}
      </div>

      {doc && (
        <div className="border-t border-[var(--color-border)] pt-4">
          <h3 className="mb-3 text-sm font-semibold text-[var(--color-header)]">Sending</h3>
          <QuoteSendPanel
            ticketId={ticketId}
            onAcceptedChange={setAccepted}
            reloadKey={sendReloadKey}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The client's address, asked for where it is needed.
 *
 * The agreements name the client and its registered address, filled in
 * from the client record when the quote is sent - and a quote whose
 * agreements would go out with a blank party is refused at send. So when
 * the record has no address this asks for it here, while the quote is
 * being written, instead of failing later and sending the agent off to
 * another page. It saves to the client record, not the quote: it is the
 * company's address, and the next quote should not ask again.
 */
function ClientAddressPrompt({
  organizationId,
  organizationName,
  onSaved,
}: {
  organizationId: string;
  organizationName: string;
  onSaved: () => void | Promise<void>;
}): React.ReactElement {
  const [form, setForm] = useState({
    addressLine1: '',
    addressLine2: '',
    addressCity: '',
    addressRegion: '',
    addressPostalCode: '',
    addressCountry: 'United States',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = (key: keyof typeof form, label: string, wide = false): React.ReactElement => (
    <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="mb-1 block text-xs font-medium text-amber-900">{label}</span>
      <input
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className={inputClass}
        aria-label={`Client ${label.toLowerCase()}`}
      />
    </label>
  );
  return (
    <div className="rounded-[var(--radius-card)] border border-amber-200 bg-amber-50 p-4">
      <p className="text-sm font-semibold text-amber-900">
        The agreements need {organizationName}&apos;s registered address.
      </p>
      <p className="mt-1 text-sm text-amber-900">
        The MNDA and MSA name the client and its address. None is on file, so the quote cannot be
        sent until it is. It is saved to the client record.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {field('addressLine1', 'Street address', true)}
        {field('addressLine2', 'Suite or unit', true)}
        {field('addressCity', 'City')}
        {field('addressRegion', 'State or region')}
        {field('addressPostalCode', 'Postal code')}
        {field('addressCountry', 'Country')}
      </div>
      {error && <p className="mt-2 text-sm text-[var(--color-danger)]">{error}</p>}
      <div className="mt-3">
        <Button
          disabled={busy || !form.addressLine1.trim() || !form.addressCity.trim()}
          onClick={() => {
            setBusy(true);
            setError(null);
            const trimmed = Object.fromEntries(
              Object.entries(form).map(([k, v]) => [k, v.trim() || null]),
            );
            apiFetch(`/api/organizations/${organizationId}`, { method: 'PATCH', json: trimmed })
              .then(() => onSaved())
              .catch((e: unknown) =>
                setError(e instanceof ApiError ? e.message : 'Could not save the address.'),
              )
              .finally(() => setBusy(false));
          }}
        >
          {busy ? 'Saving…' : 'Save address'}
        </Button>
      </div>
    </div>
  );
}

/**
 * The plan ladder, editable.
 *
 * A block-hours quote does not have a price — it has four, and the
 * client picks one. That is a different shape from the line-item table,
 * so it gets its own editor rather than being bent into rows.
 *
 * Ids are generated once and then left alone. A selection on a sent
 * quote is resolved against the id in the frozen snapshot, so renaming
 * "10 Hours" is safe and re-keying it would not be.
 */
export function PlanEditor({
  plans,
  readOnly,
  onChange,
}: {
  plans: QuotePlanOption[];
  readOnly: boolean;
  onChange: (plans: QuotePlanOption[]) => void;
}): React.ReactElement {
  const set = (index: number, patch: Partial<QuotePlanOption>): void =>
    onChange(plans.map((p, i) => (i === index ? { ...p, ...patch } : p)));

  return (
    <div className="mt-8">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold text-[var(--color-header)]">
          Plans the client chooses from
        </h2>
        {!readOnly && (
          <button
            type="button"
            onClick={() =>
              onChange([
                ...plans,
                {
                  id: `plan-${Date.now().toString(36)}`,
                  name: '',
                  hours: 0,
                  monthlyPriceUsd: 0,
                  inclusions: [],
                },
              ])
            }
            className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add plan
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-[var(--color-muted)]">
        The client picks one and the total follows. Hours are the block allowance, so they become
        the budget the team tracks time against.
      </p>

      <div className="space-y-3">
        {plans.map((plan, index) => (
          <div
            key={plan.id}
            className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-4"
          >
            <div className="grid gap-3 sm:grid-cols-[1fr_7rem_9rem_auto]">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-[var(--color-muted)]">
                  Name
                </span>
                <input
                  value={plan.name}
                  disabled={readOnly}
                  onChange={(e) => set(index, { name: e.target.value })}
                  className={inputClass}
                  aria-label={`Plan ${index + 1} name`}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-[var(--color-muted)]">
                  Hours
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={plan.hours}
                  disabled={readOnly}
                  onChange={(e) => set(index, { hours: Number(e.target.value) || 0 })}
                  className={inputClass}
                  aria-label={`Plan ${index + 1} hours`}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-[var(--color-muted)]">
                  Per month
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={plan.monthlyPriceUsd}
                  disabled={readOnly}
                  onChange={(e) => set(index, { monthlyPriceUsd: Number(e.target.value) || 0 })}
                  className={inputClass}
                  aria-label={`Plan ${index + 1} price`}
                />
              </label>
              {!readOnly && (
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => onChange(plans.filter((_, i) => i !== index))}
                    className="rounded p-2 text-[var(--color-muted)] hover:bg-rose-50 hover:text-[var(--color-danger)]"
                    aria-label={`Remove plan ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>

            {/* One per line, the same way lists are edited everywhere else
                here — writing four bullets is typing, not clicking. */}
            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-muted)]">
                What is included — one per line
              </span>
              <textarea
                rows={Math.min(8, Math.max(3, plan.inclusions.length + 1))}
                value={plan.inclusions.join('\n')}
                disabled={readOnly}
                onChange={(e) =>
                  set(index, {
                    inclusions: e.target.value.split('\n').filter((l) => l.trim() !== ''),
                  })
                }
                className={inputClass}
                aria-label={`Plan ${index + 1} inclusions`}
              />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
